import { useStore } from "@/stores/useStore";
import { useIsAuthenticated } from "@/hooks/useIsAuthenticated";
import { Loading } from "@/components/Page/Loading";
import { Forbidden } from "@/components/Page/Forbidden";
import { useRoles } from "@/hooks/useRoles";
import { useFeature } from "flagged";
import { FEATURE_FLAG } from "@/repositories/featureFlags";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/Base/Tabs";
import { SyncSettings } from "@/components/Settings/Cards/SyncSettings";
import { TimerSettings } from "@/components/Settings/Cards/TimerSettings";
import { CardLayoutSettings } from "@/components/Settings/Cards/CardLayoutSettings";
import { ColorConfigurationSettings } from "@/components/Settings/Cards/ColorConfigurationSettings";
import { ManualCardSettings } from "@/components/Settings/Cards/ManualCardSettings";
import { TagSettings } from "@/components/Settings/Cards/TagSettings";
import { CustomLabelSettings } from "@/components/Settings/Cards/CustomLabelSettings";
import { GoListConfig } from "@/components/GoList/GoListConfig";
import { useMemo } from "react";
// Import default colors

const CardConfigPage = () => {
  const { isSuperAdmin, isAdmin, isOffice, isInitialized } = useRoles();
  useIsAuthenticated();

  const shopId = useStore((s) => s.shopId);
  const { shop: shopInfo, syncWithSource, integration } = useStore();

  const isManualCardOnlyEnabledFF = useFeature(
    FEATURE_FLAG.isManualCardOnlyEnabled
  );
  const isManualCardsEnabledFF = useFeature(FEATURE_FLAG.isManualCardsEnabled);

  const isManualCardsEnabled =
    isManualCardsEnabledFF || isManualCardOnlyEnabledFF;

  const completeContainers =
    shopInfo?.cardConfig?.goListCompleteContainers ?? [];

  const containers = useMemo(() => shopInfo?.containers ?? [], [shopInfo]);

  if (!isInitialized) return <Loading />;
  if (isInitialized && !(isSuperAdmin || isAdmin || isOffice))
    return <Forbidden />;

  if (!shopInfo) return <Loading />;

  return (
    <div className="mx-auto w-full max-w-screen-xl flex-grow p-5">
      <h1 className="text-2xl">Card Settings</h1>
      <div className="flex flex-row items-start justify-between gap-2 p-4">
        <Tabs
          defaultValue={shopInfo.integration === "manual" ? "timers" : "sync"}
          className="w-full"
        >
          <TabsList variant={"primary-alt"}>
            {shopInfo.integration === "manual" ? null : (
              <TabsTrigger value="sync" variant="primary-alt">
                Status & Sync
              </TabsTrigger>
            )}
            <TabsTrigger value="timers" variant="primary-alt">
              Timers
            </TabsTrigger>
            <TabsTrigger value="cardLayout" variant="primary-alt">
              Card Layout
            </TabsTrigger>
            <TabsTrigger value="cardColors" variant="primary-alt">
              Card Colors
            </TabsTrigger>
            {isManualCardsEnabled ? (
              <TabsTrigger value="cardOptions" variant="primary-alt">
                Manual Card Options
              </TabsTrigger>
            ) : null}
            <TabsTrigger value="tagManagement" variant="primary-alt">
              Tag Management
            </TabsTrigger>
            <TabsTrigger value="goList" variant="primary-alt">
              Go List
            </TabsTrigger>
          </TabsList>
          <TabsContent value="sync">
            <div className="flex w-full flex-col gap-4 rounded-xl border border-primary-foreground p-4">
              <SyncSettings
                shopData={shopInfo}
                syncWithSource={syncWithSource}
              />
              <CustomLabelSettings
                shopData={shopInfo}
                syncWithSource={syncWithSource}
                integration={integration}
              />
            </div>
          </TabsContent>
          <TabsContent value="timers">
            <div className="flex w-full flex-col gap-4 rounded-xl border border-primary-foreground p-4">
              <TimerSettings shopData={shopInfo} />
            </div>
          </TabsContent>
          <TabsContent value="cardLayout">
            <div className="flex w-full flex-col gap-4 rounded-xl border border-primary-foreground p-4">
              <CardLayoutSettings shopData={shopInfo} />
            </div>
          </TabsContent>
          <TabsContent value="cardColors">
            <div className="flex w-full flex-col gap-4 rounded-xl border border-primary-foreground p-4">
              <ColorConfigurationSettings
                shopData={shopInfo}
                syncWithSource={syncWithSource}
              />
            </div>
          </TabsContent>
          <TabsContent value="cardOptions">
            <div className="flex w-full flex-col gap-4 rounded-xl border border-primary-foreground p-4">
              <ManualCardSettings shopData={shopInfo} />
            </div>
          </TabsContent>
          <TabsContent value="tagManagement">
            <div className="flex w-full flex-col gap-4 rounded-xl border border-primary-foreground p-4">
              <TagSettings
                shopData={shopInfo}
                syncWithSource={syncWithSource}
              />
            </div>
          </TabsContent>
          <TabsContent value="goList">
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Go List Configuration</h2>
              <GoListConfig
                shopId={shopId}
                shopInfo={shopInfo}
                completeContainers={completeContainers}
                containers={containers}
              />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default CardConfigPage;
